// src/lib/propertyManagement.ts — the development page's Property management tab: its calls and labels.
import { api } from './api';
import type { CertificateHistoryItem, CertificateTypeInfo, ComplianceGrid, ManagementSummary, PeriodicVisits, WorkOrderChargeTo, WorkOrderRow } from '../types/api';

export const pmApi = {
  summary: async (propertyId: string) => (await api.get<ManagementSummary>(`/properties/${propertyId}/management-summary`)).data,
  grid: async (propertyId: string) => (await api.get<ComplianceGrid>('/compliance/grid', { propertyId })).data,
  history: async (certificateId: string) => (await api.get<CertificateHistoryItem[]>(`/compliance/${certificateId}/history`)).data,
  visits: async (propertyId: string) => (await api.get<PeriodicVisits>('/inspections/periodic-visits', { propertyId })).data,
  certificateTypes: async () => (await api.get<CertificateTypeInfo[]>('/compliance/types')).data,
};

/** Who pays — the demo's "Who pays" list, in the API's values. */
export const CHARGE_TO: Array<[WorkOrderChargeTo, string]> = [
  ['LANDLORD', 'Landlord'], ['TENANT', 'Tenant'], ['SERVICE_CHARGE', 'Service charge'], ['WARRANTY', 'Warranty / developer'], ['INSURANCE', 'Insurance claim'],
];
export const chargeToLabel = (v: string | null | undefined) => CHARGE_TO.find(([k]) => k === v)?.[1] ?? null;

/** Trade categories a job is raised under (the work-order form and the AI triage use the same list). */
export const WORK_ORDER_CATEGORIES = ['PLUMBING', 'HEATING', 'ELECTRICAL', 'GAS', 'LIFT', 'FIRE', 'DOORS_WINDOWS', 'DAMP', 'PEST', 'CLEANING', 'GENERAL', 'OTHER'];
export const PRIORITIES: Array<['EMERGENCY' | 'URGENT' | 'ROUTINE' | 'PLANNED', string, string]> = [
  ['EMERGENCY', 'Emergency', 'Attend within 24 hours'], ['URGENT', 'Urgent', 'Attend within 3 days'],
  ['ROUTINE', 'Routine', 'Attend within 14 days'], ['PLANNED', 'Planned', 'Programmed work'],
];

export const humanise = (s: string | null | undefined) => (s ? s.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase()) : '');

/** Whole days from today to an ISO date or timestamp (negative = past). */
export function daysTo(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const today = Date.parse(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`);
  return Math.round((Date.parse(`${iso.slice(0, 10)}T00:00:00Z`) - today) / 86_400_000);
}

/** The cell / chip colours: theme-aware CSS variables (index.css), so they hold in dark mode. */
export const CELL_TONE: Record<string, { color: string; label: string }> = {
  VALID: { color: 'var(--status-good)', label: 'In date' },
  DUE_SOON: { color: 'var(--status-warning)', label: 'Due soon' },
  EXPIRED: { color: 'var(--status-critical)', label: 'Expired' },
  NOT_RECORDED: { color: 'var(--status-neutral)', label: 'Not recorded' },
};
export const TONE_COLOR: Record<'critical' | 'warning' | 'info' | 'violet', string> = {
  critical: 'var(--status-critical)', warning: 'var(--status-warning)', info: 'var(--color-sky-600)', violet: 'var(--color-violet-600)',
};

// ── Works on the property tab ──
/** The stat tiles' filters on this list (keys are the ?pmf= values). */
export const WORK_FILTERS = {
  open: { label: 'Open works', params: { open: true } },
  overdue: { label: 'Overdue — past target', params: { overdue: true } },
  emergency: { label: 'Open emergencies', params: { open: true, priority: 'EMERGENCY' } },
  completed: { label: 'Completed in the last 90 days', params: { open: false, status: 'COMPLETED,VERIFIED,CLOSED', completedFrom: new Date(Date.now() - 90 * 86_400_000).toISOString().slice(0, 10) } },
} as const;
export type WorkFilter = keyof typeof WORK_FILTERS;
/** The Work orders sub-tab's tile filters (jobs raised to a contractor); no tile = every work order. */
export const ORDER_FILTERS = {
  open: { label: 'Open work orders', params: { open: true } },
  booked: { label: 'Open and booked in', params: { booked: true } },
} as const;
export type OrderFilter = keyof typeof ORDER_FILTERS;

export const PRIORITY_COLOUR: Record<string, string> = {
  EMERGENCY: 'var(--status-critical)', URGENT: 'var(--status-warning)', ROUTINE: 'var(--color-sky-600)', PLANNED: 'var(--status-neutral)',
};
/** Days past the resolve-by target, for an open job; null when it is not late. */
export const overdueDays = (w: WorkOrderRow) => {
  if (!w.isOpen) return null;
  const d = daysTo(w.slaResolveBy);
  if (d !== null && d < 0) return -d;
  return w.slaBreached ? 0 : null;
};
/** How loudly a row shouts (demo jobRowColour): late and getting later → red; otherwise the priority; done → settles. */
export function workRowColour(w: WorkOrderRow): string {
  const late = overdueDays(w);
  if (late !== null) return late > 7 ? 'var(--status-critical)' : 'var(--status-serious)';
  if (!w.isOpen) return w.status === 'CANCELLED' ? 'var(--status-neutral)' : 'var(--status-good)';
  return PRIORITY_COLOUR[w.priority] ?? 'var(--status-neutral)';
}
